/**
 * 移动端商品编辑页
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  TextArea,
  Button,
  Card,
  Stepper,
  ImageUploader,
  Selector,
  Skeleton,
  Collapse,
  Dialog,
} from 'antd-mobile';
import { adminProductApi } from '../../api/index';
import Toast from 'antd-mobile/es/components/toast';
import RichTextEditor from '../../components/RichTextEditor';

export default function MobileProductFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [variants, setVariants] = useState([]);
  const [images, setImages] = useState([]);
  const [originalImages, setOriginalImages] = useState([]); // 保存原始图片对象（含 id）

  useEffect(() => {
    if (!isNew) {
      fetchProduct();
    }
  }, [id]);

  const fetchProduct = async () => {
    try {
      const data = await adminProductApi.getById(id);
      form.setFieldsValue({
        title: data.title,
        slug: data.slug,
        description: data.description,
        bodyHtml: data.bodyHtml,
        vendor: data.vendor,
        productType: data.productType,
        status: data.status,
        tags: data.tags,
        seoTitle: data.seoTitle,
        seoDescription: data.seoDescription,
      });
      setVariants(data.variants || []);
      // 保存原始图片对象（含 id）和显示用的图片列表
      const originalImgs = data.images || [];
      setOriginalImages(originalImgs);
      setImages(originalImgs.map((img) => ({
        url: img.url,
        imageId: img.id, // 保存 imageId 用于删除
      })));
    } catch (err) {
      Toast.show('商品不存在');
      navigate('/admin/m/products');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      // 处理图片上传：对于新建商品，图片需要先上传临时存储
      let uploadedImages = [];
      if (isNew && images.length > 0) {
        // 新建商品时，先创建商品，再上传图片
        // 这里暂时不处理图片，用户可以在创建后再上传
        uploadedImages = [];
      } else {
        // 编辑商品时，使用已有的图片 URL
        uploadedImages = images.map((img) => ({ url: img.url }));
      }

      const payload = {
        ...values,
        variants: variants.map((v, index) => ({
          ...v,
          sku: v.sku ? v.sku.trim() : `auto-${Date.now()}-${index}`,
          price: Number(v.price) || 0,
          compareAtPrice: v.compareAtPrice ? Number(v.compareAtPrice) : null,
          inventoryQty: Number(v.inventoryQty) || 0,
        })),
        images: uploadedImages,
      };

      if (isNew) {
        const created = await adminProductApi.create(payload);
        // 创建成功后，如果有图片，需要上传
        if (images.length > 0 && created.id) {
          for (const img of images) {
            if (img.file) {
              const formData = new FormData();
              formData.append('file', img.file);
              await adminProductApi.uploadImage(created.id, formData);
            }
          }
        }
        Toast.show('创建成功');
      } else {
        await adminProductApi.update(id, payload);
        // 上传新添加的图片
        for (const img of images) {
          if (img.file && !img.imageId) {
            const formData = new FormData();
            formData.append('file', img.file);
            await adminProductApi.uploadImage(id, formData);
          }
        }
        Toast.show('保存成功');
      }
      navigate('/admin/m/products');
    } catch (err) {
      Toast.show(err?.response?.data?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const addVariant = () => {
    setVariants([
      ...variants,
      {
        title: `Variant ${variants.length + 1}`,
        sku: '',
        price: 0,
        compareAtPrice: 0,
        inventoryQty: 0,
        option1Name: '',
        option1Value: '',
        option2Name: '',
        option2Value: '',
      },
    ]);
  };

  const updateVariant = (index, field, value) => {
    const newVariants = [...variants];
    newVariants[index] = { ...newVariants[index], [field]: value };
    setVariants(newVariants);
  };

  const removeVariant = (index) => {
    const newVariants = variants.filter((_, i) => i !== index);
    setVariants(newVariants);
  };

  // 处理图片变化（包括删除）
  const handleImageChange = (newImages) => {
    // 检查是否有图片被删除
    const deletedImages = images.filter(
      (img) => !newImages.some((newImg) => newImg.url === img.url)
    );

    // 删除后端图片
    deletedImages.forEach(async (img) => {
      if (img.imageId) {
        try {
          await adminProductApi.deleteImage(img.imageId);
          console.log('删除图片成功:', img.imageId);
        } catch (err) {
          console.error('删除图片失败:', err);
        }
      }
    });

    setImages(newImages);
  };

  if (loading) return <div style={{ padding: 16 }}><Skeleton.Title rows={5} /></div>;

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      {/* 顶部固定操作栏 - fixed 定位，始终在屏幕顶部 */}
      <div
        style={{
          position: 'fixed',
          top: 45, // 在 MobileLayout NavBar 下方
          left: 0,
          right: 0,
          zIndex: 99,
          background: '#fff',
          borderBottom: '1px solid #eee',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span style={{ fontSize: 16, fontWeight: 600 }}>
          {isNew ? '新建商品' : '编辑商品'}
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            color="primary"
            size="small"
            loading={saving}
            onClick={() => form.submit()}
          >
            保存
          </Button>
          <Button
            size="small"
            variant="outline"
            onClick={() => navigate('/admin/m/products')}
          >
            取消
          </Button>
        </div>
      </div>

      {/* 内容区域 - 顶部留出空间 */}
      <div style={{ padding: 16, paddingTop: 70, paddingBottom: 60 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
        {/* 基本信息 */}
        <Card title="基本信息" style={{ marginBottom: 16 }}>
          <Form.Item
            name="title"
            label="商品名称"
            rules={[{ required: true, message: '请输入商品名称' }]}
          >
            <Input placeholder="请输入商品名称" />
          </Form.Item>

          <Form.Item name="slug" label="自定义 URL">
            <Input placeholder="留空自动生成" />
          </Form.Item>

          <Form.Item name="description" label="商品描述">
            <TextArea placeholder="请输入商品描述" rows={3} />
          </Form.Item>

          <Form.Item name="bodyHtml" label="商品详情">
            <RichTextEditor simple placeholder="商品详情，支持富文本" />
          </Form.Item>

          {/* 商品图片 - 移动到商品详情下方 */}
          <Form.Item label="商品图片">
            <ImageUploader
              value={images}
              onChange={handleImageChange}
              maxCount={9}
              upload={async (file) => {
                try {
                  if (isNew) {
                    // 新建商品时，先临时显示，保存后再上传
                    return { url: URL.createObjectURL(file), file };
                  } else {
                    // 编辑商品时，立即上传
                    const formData = new FormData();
                    formData.append('file', file);
                    const result = await adminProductApi.uploadImage(id, formData);
                    return { url: result.url, imageId: result.id };
                  }
                } catch (err) {
                  Toast.show('上传失败');
                  throw err;
                }
              }}
              onDelete={(file) => {
                if (file.imageId) {
                  Dialog.confirm({
                    content: '确定删除这张图片吗？',
                    onConfirm: async () => {
                      try {
                        await adminProductApi.deleteImage(file.imageId);
                        Toast.show('删除成功');
                      } catch (err) {
                        Toast.show('删除失败');
                      }
                    },
                  });
                }
                return true;
              }}
            />
          </Form.Item>

          <Form.Item name="vendor" label="供应商">
            <Input placeholder="请输入供应商" />
          </Form.Item>

          <Form.Item name="productType" label="商品类型">
            <Input placeholder="请输入商品类型" />
          </Form.Item>

          <Form.Item
            name="status"
            label="状态"
            initialValue="active"
          >
            <Selector
              options={[
                { label: '上架中', value: 'active' },
                { label: '已下架', value: 'draft' },
              ]}
            />
          </Form.Item>

          <Form.Item name="tags" label="标签">
            <Input placeholder="用逗号分隔多个标签" />
          </Form.Item>

          <Form.Item name="seoTitle" label="SEO 标题">
            <Input placeholder="搜索引擎显示的标题" />
          </Form.Item>

          <Form.Item name="seoDescription" label="SEO 描述">
            <TextArea placeholder="搜索引擎描述（150 字以内）" rows={2} />
          </Form.Item>
        </Card>

        {/* 变体管理 */}
        <Card title="变体管理" style={{ marginBottom: 16 }}>
          {variants.map((variant, index) => (
            <div
              key={index}
              style={{
                border: '1px solid #eee',
                borderRadius: 8,
                padding: 12,
                marginBottom: 12,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontWeight: 500 }}>{variant.title || `变体 ${index + 1}`}</span>
                <span
                  style={{ color: '#ff4d4f', cursor: 'pointer' }}
                  onClick={() => removeVariant(index)}
                >
                  删除
                </span>
              </div>

              <Form.Item label="名称">
                <Input
                  value={variant.title}
                  onChange={(v) => updateVariant(index, 'title', v)}
                  placeholder="变体名称"
                />
              </Form.Item>

              <Form.Item label="SKU">
                <Input
                  value={variant.sku}
                  onChange={(v) => updateVariant(index, 'sku', v)}
                  placeholder="SKU"
                />
              </Form.Item>

              <Form.Item label="价格 ($)">
                <Stepper
                  value={variant.price}
                  onChange={(v) => updateVariant(index, 'price', v)}
                  min={0}
                  step={0.01}
                  precision={2}
                />
              </Form.Item>

              <Form.Item label="划线价 ($)">
                <Stepper
                  value={variant.compareAtPrice || 0}
                  onChange={(v) => updateVariant(index, 'compareAtPrice', v)}
                  min={0}
                  step={0.01}
                  precision={2}
                />
              </Form.Item>

              <Form.Item label="库存">
                <Stepper
                  value={variant.inventoryQty || 0}
                  onChange={(v) => updateVariant(index, 'inventoryQty', v)}
                  min={0}
                />
              </Form.Item>

              {/* 属性选项 - 折叠面板 */}
              <Collapse>
                <Collapse.Panel key="options" header="属性选项（颜色/尺码等）">
                  <div style={{ fontSize: 13, fontWeight: 500, margin: '8px 0' }}>选项 1</div>
                  <Form.Item label="选项名">
                    <Input
                      value={variant.option1Name || ''}
                      onChange={(v) => updateVariant(index, 'option1Name', v)}
                      placeholder="如：颜色"
                    />
                  </Form.Item>
                  <Form.Item label="选项值">
                    <Input
                      value={variant.option1Value || ''}
                      onChange={(v) => updateVariant(index, 'option1Value', v)}
                      placeholder="如：红色"
                    />
                  </Form.Item>

                  <div style={{ fontSize: 13, fontWeight: 500, margin: '8px 0' }}>选项 2</div>
                  <Form.Item label="选项名">
                    <Input
                      value={variant.option2Name || ''}
                      onChange={(v) => updateVariant(index, 'option2Name', v)}
                      placeholder="如：尺码"
                    />
                  </Form.Item>
                  <Form.Item label="选项值">
                    <Input
                      value={variant.option2Value || ''}
                      onChange={(v) => updateVariant(index, 'option2Value', v)}
                      placeholder="如：XL"
                    />
                  </Form.Item>
                </Collapse.Panel>
              </Collapse>
            </div>
          ))}

          <Button block variant="outline" onClick={addVariant}>
            添加变体
          </Button>
        </Card>
      </Form>
    </div>
  </div>
  );
}
